import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';
import { RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Widgets
import {
  KPISummaryCards,
  TeamStatusGrid,
  PendingApprovalsWidget,
  GeofenceComplianceWidget,
  AttendanceTrendChart,
  HoursDistributionChart,
  LocationHeatmap,
  LocationTrailMap,
} from './widgets';

// Hooks
import {
  useDashboardData,
  useTeamStatus,
  useAttendanceTrends,
  useGeofenceViolations,
  usePendingApprovals,
  useHoursDistribution,
  useLocationHeatmap,
  useUserLocationTrail,
} from './hooks/useDashboardData';
import { useRoleAccess } from './hooks/useRoleAccess';

/**
 * Enhanced Dashboard - Role-based dashboard with modern widgets
 * Supports Super Admin viewing data as specific organization
 */
const EnhancedDashboard = () => {
  const { userRole, isAdmin, isManager, isMember, isSuperAdmin, superAdminSelectedOrgId, superAdminSelectedSubOrgId, canViewWidget, userId, orgId, userName } = useRoleAccess();
  const navigate = useNavigate();

  // Get global location filter from Redux (shared with header)
  const selectedLocationId = useSelector((state) => state.selectedLocationFilter);

  // Determine if we should pass orgId to API calls (Super Admin viewing as specific org)
  const effectiveOrgId = isSuperAdmin && superAdminSelectedOrgId ? superAdminSelectedOrgId : null;
  // When the Super Admin OR a root Admin has drilled into a specific
  // SUB-ORG, scope every widget to that sub-org only (never the parent org
  // or siblings). Uses a precise userRole check (not the hook's isAdmin,
  // which is also true for "Super Admin viewing as org") to avoid double-
  // applying alongside the isSuperAdmin case above.
  const effectiveSubOrgId = (isSuperAdmin || userRole === 'ADMIN') && superAdminSelectedSubOrgId ? superAdminSelectedSubOrgId : null;

  // Date range state
  const [dateRange, setDateRange] = useState({
    fromDate: dayjs().startOf('week').format('YYYY-MM-DD'),
    toDate: dayjs().endOf('week').format('YYYY-MM-DD'),
  });

  // Location trail state
  const [trailDate, setTrailDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [selectedMemberForTrail, setSelectedMemberForTrail] = useState(null);

  // Fetch dashboard data using custom hooks with location filter and org_id for Super Admin
  const { summary, loading: summaryLoading, refetch: refetchSummary } = useDashboardData(effectiveOrgId, selectedLocationId, effectiveSubOrgId);

  const { teamMembers, loading: teamLoading, refetch: refetchTeam } = useTeamStatus({
    status: 'ALL',
  }, selectedLocationId, effectiveOrgId, effectiveSubOrgId);

  const { trends, totalEmployees, loading: trendsLoading, refetch: refetchTrends } = useAttendanceTrends(
    dateRange.fromDate,
    dateRange.toDate,
    [],
    selectedLocationId,
    effectiveOrgId,
    effectiveSubOrgId
  );

  const { violations, totalCount: violationsCount, loading: violationsLoading, refetch: refetchViolations } = useGeofenceViolations({
    fromDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    toDate: dayjs().format('YYYY-MM-DD'),
    resolved: false,
    pageNumber: 1,
    pageSize: 10,
  }, selectedLocationId, effectiveOrgId, effectiveSubOrgId);

  const { approvals, totalPending, loading: approvalsLoading, refetch: refetchApprovals } = usePendingApprovals(selectedLocationId, effectiveOrgId, effectiveSubOrgId);

  const { distribution, loading: distributionLoading, refetch: refetchDistribution } = useHoursDistribution(
    dateRange.fromDate,
    dateRange.toDate,
    selectedLocationId,
    effectiveOrgId,
    effectiveSubOrgId
  );

  // Location heatmap data - Admin only (supports location filtering and org_id for Super Admin)
  const { locations, loading: locationsLoading, refetch: refetchLocations } = useLocationHeatmap(null, selectedLocationId, effectiveOrgId, effectiveSubOrgId);

  // Location trail - for selected member or self
  const trailUserId = selectedMemberForTrail?.user_id || userId;
  const { trail, loading: trailLoading, refetch: refetchTrail } = useUserLocationTrail(trailUserId, trailDate);

  // Handle date range change
  const handleDateRangeChange = useCallback((fromDate, toDate) => {
    setDateRange({ fromDate, toDate });
  }, []);

  // Refresh all data
  const handleRefreshAll = useCallback(() => {
    refetchSummary();
    refetchTeam();
    refetchTrends();
    refetchViolations();
    refetchApprovals();
    refetchDistribution();
    if (isAdmin) {
      refetchLocations();
    }
    refetchTrail();
  }, [refetchSummary, refetchTeam, refetchTrends, refetchViolations, refetchApprovals, refetchDistribution, refetchLocations, refetchTrail, isAdmin]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const interval = setInterval(handleRefreshAll, 60000);
    return () => clearInterval(interval);
  }, [handleRefreshAll]);

  // Navigation handlers
  const handleViewAllApprovals = () => {
    navigate('/home/approvaltimeentries');
  };

  const handleViewAllViolations = () => {
    navigate('/home/locationTracking');
  };

  const handleMemberClick = (member) => {
    setSelectedMemberForTrail(member);
  };

  const handleResolveViolation = async (violationId) => {
    console.log('Resolve violation:', violationId);
    refetchViolations();
  };

  const handleLocationClick = (location) => {
    console.log('Location clicked:', location);
  };

  const handleTrailDateChange = (newDate) => {
    setTrailDate(newDate);
  };

  // Show message for Super Admin to select an organization
  if (isSuperAdmin && !superAdminSelectedOrgId) {
    return (
      <div className="p-4 bg-gray-50 min-h-screen">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
            <p className="text-sm text-gray-1000">
              {dayjs().format('dddd, MMMM D, YYYY')} • {userRole} View
            </p>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center bg-white rounded-lg shadow-sm p-12 mt-8">
          <div className="text-amber-1000 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Select an Organization</h2>
          <p className="text-gray-1000 text-center max-w-md">
            Please select an organization from the dropdown in the header to view its analytics dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Analytics</h1>
          <p className="text-sm text-gray-1000">
            {dayjs().format('dddd, MMMM D, YYYY')} • {userRole} View
            {isSuperAdmin && superAdminSelectedOrgId && ' (Viewing as Organization)'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefreshAll}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium text-gray-700"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Summary Cards - Admin & Manager */}
      {canViewWidget('KPI_SUMMARY') && (
        <KPISummaryCards summary={summary} loading={summaryLoading} />
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-4">
          {/* Attendance Trends - First (Primary focus) */}
          {(canViewWidget('ATTENDANCE_TRENDS_ORG') || canViewWidget('ATTENDANCE_TRENDS_TEAM')) && (
            <AttendanceTrendChart
              trends={trends}
              totalEmployees={totalEmployees}
              loading={trendsLoading}
              fromDate={dateRange.fromDate}
              toDate={dateRange.toDate}
              onDateRangeChange={handleDateRangeChange}
              periodType="week"
            />
          )}

          {/* Location Heatmap - Second */}
          {canViewWidget('LOCATION_HEATMAP') && (
            <LocationHeatmap
              locations={locations}
              loading={locationsLoading}
              onRefresh={refetchLocations}
              onLocationClick={handleLocationClick}
            />
          )}

          {/* Team Status Grid - Same width as Location Heatmap */}
          {canViewWidget('TEAM_STATUS_GRID') && (
            <TeamStatusGrid
              teamMembers={teamMembers}
              loading={teamLoading}
              onRefresh={refetchTeam}
              onMemberClick={handleMemberClick}
            />
          )}
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-4">
          {/* Pending Approvals - Admin & Manager */}
          {canViewWidget('PENDING_APPROVALS') && (
            <PendingApprovalsWidget
              approvals={approvals}
              totalPending={totalPending}
              loading={approvalsLoading}
              onViewAll={handleViewAllApprovals}
            />
          )}

          {/* Geofence Compliance - Admin & Manager */}
          {canViewWidget('GEOFENCE_VIOLATIONS') && (
            <GeofenceComplianceWidget
              violations={violations}
              totalCount={violationsCount}
              loading={violationsLoading}
              onViewAll={handleViewAllViolations}
              onResolve={handleResolveViolation}
            />
          )}

          {/* Hours Distribution */}
          {canViewWidget('HOURS_DISTRIBUTION') && (
            <HoursDistributionChart
              distribution={distribution}
              loading={distributionLoading}
              periodLabel={`${dayjs(dateRange.fromDate).format('MMM D')} - ${dayjs(dateRange.toDate).format('MMM D')}`}
            />
          )}

          {/* Location Trail - All users */}
          {canViewWidget('LOCATION_TRAIL') && (
            <LocationTrailMap
              trail={trail}
              loading={trailLoading}
              selectedDate={trailDate}
              onDateChange={handleTrailDateChange}
              userName={selectedMemberForTrail?.full_name || userName}
            />
          )}
        </div>
      </div>

      {/* Member View - Personal Dashboard */}
      {isMember && !isManager && !isAdmin && (
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Personal Location Trail */}
          <LocationTrailMap
            trail={trail}
            loading={trailLoading}
            selectedDate={trailDate}
            onDateChange={handleTrailDateChange}
            userName={userName}
          />

          {/* Personal Hours */}
          <HoursDistributionChart
            distribution={distribution}
            loading={distributionLoading}
            periodLabel="This Week"
          />
        </div>
      )}
    </div>
  );
};

export default EnhancedDashboard;
